import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/admin/ProductForm.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    FormControlLabel,
    Switch,
    Box,
    Typography,
    Chip,
    InputAdornment,
    Autocomplete,
    Collapse,
    Divider
} from '@mui/material';
import ImageUpload from '../common/ImageUpload';
// Iconos para mejorar la UI de los toggles
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ProductForm = ({ open, onClose, onSave, product, categories = [] }) => {
  const { t } = useTranslation();
    // Estado del formulario
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        short_description: '',
        sku: '',
        category_id: '',
        price: '',
        compare_price: '',
        cost: '',
        stock_quantity: 0,
        track_inventory: true,
        allow_backorder: false,
        weight: '',
        dimensions: { length: '', width: '', height: '' },
        is_active: true,
        is_featured: false,
        is_digital: false,
        meta_title: '',
        meta_description: '',
        images: [],
        main_image: '',
        specifications: {},
        compatibility: {}
    });

    // Estados para inputs dinámicos
    const [specKey, setSpecKey] = useState('');
    const [specValue, setSpecValue] = useState('');
    const [compatKey, setCompatKey] = useState('');
    const [compatValue, setCompatValue] = useState('');
    const [errors, setErrors] = useState({});

    // Estados de visibilidad (Toggles)
    const [showCost, setShowCost] = useState(false);
    const [showPhysical, setShowPhysical] = useState(false);
    const [showSEO, setShowSEO] = useState(false);

    useEffect(() => {
        if (product) {
            // Cargar datos existentes
            setFormData({
                ...product,
                dimensions: product.dimensions || { length: '', width: '', height: '' },
                specifications: product.specifications || {},
                compatibility: product.compatibility || {},
                images: product.images || [],
                price: product.price?.toString() || '',
                compare_price: product.compare_price?.toString() || '',
                cost: product.cost?.toString() || '',
                weight: product.weight?.toString() || ''
            });

            // Lógica inteligente: Si al editar ya hay datos, mostramos las secciones
            if (product.cost) setShowCost(true);

            if (product.weight ||
                (product.dimensions && (product.dimensions.length || product.dimensions.width || product.dimensions.height))) {
                setShowPhysical(true);
            }

            if (product.meta_title || product.meta_description) setShowSEO(true);

        } else {
            // Resetear para producto nuevo
            setFormData({
                name: '',
                description: '',
                short_description: '',
                sku: '',
                category_id: '',
                price: '',
                compare_price: '',
                cost: '',
                stock_quantity: 0,
                track_inventory: true,
                allow_backorder: false,
                weight: '',
                dimensions: { length: '', width: '', height: '' },
                is_active: true,
                is_featured: false,
                is_digital: false,
                meta_title: '',
                meta_description: '',
                images: [],
                main_image: '',
                specifications: {},
                compatibility: {}
            });
            // Ocultar secciones opcionales por defecto
            setShowCost(false);
            setShowPhysical(false);
            setShowSEO(false);
        }
        setErrors({});
    }, [product, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleDimensionChange = (dimension, value) => {
        setFormData(prev => ({
            ...prev,
            dimensions: { ...prev.dimensions, [dimension]: value }
        }));
    };

    const handleImageChange = ({ images, mainImage }) => {
        setFormData(prev => ({
            ...prev,
            images,
            main_image: mainImage
        }));
    };

    const addSpecification = () => {
        if (specKey.trim() && specValue.trim()) {
            setFormData(prev => ({
                ...prev,
                specifications: { ...prev.specifications, [specKey.trim()]: specValue.trim() }
            }));
            setSpecKey('');
            setSpecValue('');
        }
    };

    const removeSpecification = (key) => {
        setFormData(prev => {
            const newSpecs = { ...prev.specifications };
            delete newSpecs[key];
            return { ...prev, specifications: newSpecs };
        });
    };

    const addCompatibility = () => {
        if (compatKey.trim() && compatValue.trim()) {
            setFormData(prev => ({
                ...prev,
                compatibility: { ...prev.compatibility, [compatKey.trim()]: compatValue.trim() }
            }));
            setCompatKey('');
            setCompatValue('');
        }
    };

    const removeCompatibility = (key) => {
        setFormData(prev => {
            const newCompat = { ...prev.compatibility };
            delete newCompat[key];
            return { ...prev, compatibility: newCompat };
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = t('hardcoded.components_admin_categoryform.nombre_es_requerido');
        if (!formData.sku.trim()) newErrors.sku = t('hardcoded.components_admin_kitform.sku_es_requerido');
        if (!formData.category_id) newErrors.category_id = t('hardcoded.components_admin_productform.categoria_es_requerida');
        if (!formData.price || parseFloat(formData.price) <= 0) {
            newErrors.price = t('hardcoded.components_admin_kitform.precio_debe_ser_mayor_a');
        }
        if (formData.compare_price && parseFloat(formData.compare_price) <= parseFloat(formData.price)) {
            newErrors.compare_price = t('hardcoded.components_admin_productform.precio_de_comparacion_debe_ser');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        // Limpiar datos si las secciones están ocultas y vacías, o formatear números
        const saveData = {
            ...formData,
            price: parseFloat(formData.price),
            compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
            // Si está oculto o vacío, enviamos null
            cost: (showCost && formData.cost) ? parseFloat(formData.cost) : null,
            weight: (showPhysical && formData.weight) ? parseFloat(formData.weight) : null,
            dimensions: (showPhysical && (formData.dimensions.length || formData.dimensions.width || formData.dimensions.height)) ?
                {
                    length: parseFloat(formData.dimensions.length) || 0,
                    width: parseFloat(formData.dimensions.width) || 0,
                    height: parseFloat(formData.dimensions.height) || 0
                } : null,
            // Si SEO está oculto, podrías decidir limpiar los campos o mantenerlos si el usuario solo colapsó la vista.
            // Aquí asumimos que los datos se guardan si existen en el form, independientemente de la vista.
        };

        onSave(saveData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {product ? t('hardcoded.components_admin_productform.editar_producto') : t('hardcoded.components_admin_productform.crear_producto')}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* --- Información Básica --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{t('audit.detail.basicInfo')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_productform.nombre_del_producto')}
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="SKU *"
                            value={formData.sku}
                            onChange={(e) => handleChange('sku', e.target.value)}
                            error={!!errors.sku}
                            helperText={errors.sku}
                        />
                    </Grid>

                    {/* CAMBIO 1: Categoría como Autocomplete (Search Dropdown) */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.category_id}>
                            <Autocomplete
                                options={categories}
                                getOptionLabel={(option) => option.name || ''}
                                value={categories.find(c => c.id === formData.category_id) || null}
                                onChange={(event, newValue) => {
                                    handleChange('category_id', newValue ? newValue.id : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('hardcoded.components_admin_productform.categoria')}
                                        error={!!errors.category_id}
                                        helperText={errors.category_id}
                                    />
                                )}
                                noOptionsText={t('hardcoded.components_admin_productform.no_se_encontraron_categorias')}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_productform.descripcion_corta')}
                            value={formData.short_description}
                            onChange={(e) => handleChange('short_description', e.target.value)}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_productform.descripcion_completa')}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            multiline
                            rows={4}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Precios --- */}
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="primary">{i18n.t('hardcoded.components_admin_kitform.precios')}</Typography>

                        {/* CAMBIO 2: Toggle para Costo */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showCost}
                                    onChange={(e) => setShowCost(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={t('hardcoded.components_admin_productform.registrar_costoganancia')}
                        />
                    </Grid>

                    <Grid item xs={12} md={showCost ? 6 : 6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_productform.precio')}
                            type="number"
                            value={formData.price}
                            onChange={(e) => handleChange('price', e.target.value)}
                            error={!!errors.price}
                            helperText={errors.price}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={showCost ? 6 : 6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_kitform.precio_de_comparacion')}
                            type="number"
                            value={formData.compare_price}
                            onChange={(e) => handleChange('compare_price', e.target.value)}
                            error={!!errors.compare_price}
                            helperText={errors.compare_price || t('hardcoded.components_admin_productform.para_mostrar_descuentos')}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    {/* Campo de Costo Ocultable */}
                    <Grid item xs={12}>
                        <Collapse in={showCost}>
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <TextField
                                    fullWidth
                                    label={t('hardcoded.components_admin_productform.costo_del_producto')}
                                    type="number"
                                    value={formData.cost}
                                    onChange={(e) => handleChange('cost', e.target.value)}
                                    helperText={t('hardcoded.components_admin_productform.solo_visible_para_administradores_usado')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                />
                            </Box>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Inventario --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{i18n.t('hardcoded.components_admin_productform.inventario')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.track_inventory}
                                    onChange={(e) => handleChange('track_inventory', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_productform.rastrear_inventario')}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.allow_backorder}
                                    onChange={(e) => handleChange('allow_backorder', e.target.checked)}
                                    disabled={!formData.track_inventory}
                                />
                            }
                            label={t('hardcoded.components_admin_productform.permitir_pedidos_pendientes')}
                        />
                    </Grid>

                    {formData.track_inventory && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_productform.cantidad_en_stock')}
                                type="number"
                                value={formData.stock_quantity}
                                onChange={(e) => handleChange('stock_quantity', parseInt(e.target.value) || 0)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Propiedades Físicas (Ocultable) --- */}
                    {/* CAMBIO 3: Toggle para Propiedades Físicas */}
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="primary">{t('hardcoded.components_admin_productform.envio_y_propiedades_fisicas')}</Typography>
                        <Button
                            onClick={() => setShowPhysical(!showPhysical)}
                            endIcon={showPhysical ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            size="small"
                        >
                            {showPhysical ? t('hardcoded.components_admin_productform.ocultar_detalles') : t('hardcoded.components_admin_productform.configurar_envio')}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Collapse in={showPhysical}>
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label={t('hardcoded.components_admin_productform.peso_kg')}
                                            type="number"
                                            value={formData.weight}
                                            onChange={(e) => handleChange('weight', e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label={t('hardcoded.components_admin_productform.largo_cm')}
                                            type="number"
                                            value={formData.dimensions.length}
                                            onChange={(e) => handleDimensionChange('length', e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label={t("hardcoded.components_admin_productform.ancho_cm")}
                                            type="number"
                                            value={formData.dimensions.width}
                                            onChange={(e) => handleDimensionChange('width', e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label={t('hardcoded.components_admin_productform.alto_cm')}
                                            type="number"
                                            value={formData.dimensions.height}
                                            onChange={(e) => handleDimensionChange('height', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Imágenes --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{t('hardcoded.components_admin_kitform.imagenes')}</Typography>
                        <ImageUpload
                            images={formData.images}
                            mainImage={formData.main_image}
                            onChange={handleImageChange}
                            maxImages={10}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Especificaciones Técnicas --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{t('product.specifications')}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('hardcoded.components_admin_productform.caracteristica')}
                                        value={specKey}
                                        onChange={(e) => setSpecKey(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('hardcoded.components_admin_productform.valor')}
                                        value={specValue}
                                        onChange={(e) => setSpecValue(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={addSpecification}
                                        disabled={!specKey.trim() || !specValue.trim()}
                                    >
                                        +
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {Object.entries(formData.specifications).map(([key, value]) => (
                                <Chip
                                    key={key}
                                    label={`${key}: ${value}`}
                                    onDelete={() => removeSpecification(key)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Grid>

                    {/* --- Compatibilidad --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{t('hardcoded.components_admin_productform.compatibilidad')}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('hardcoded.components_admin_productform.vehiculosistema')}
                                        value={compatKey}
                                        onChange={(e) => setCompatKey(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('hardcoded.components_admin_productform.detalles')}
                                        value={compatValue}
                                        onChange={(e) => setCompatValue(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={addCompatibility}
                                        disabled={!compatKey.trim() || !compatValue.trim()}
                                    >
                                        +
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {Object.entries(formData.compatibility).map(([key, value]) => (
                                <Chip
                                    key={key}
                                    label={`${key}: ${value}`}
                                    onDelete={() => removeCompatibility(key)}
                                    color="secondary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- SEO (Ocultable) --- */}
                    {/* CAMBIO 4: Toggle para SEO */}
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="primary">{t('hardcoded.components_admin_productform.optimizacion_seo')}</Typography>
                        <Button
                            onClick={() => setShowSEO(!showSEO)}
                            endIcon={showSEO ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            size="small"
                        >
                            {showSEO ? t('hardcoded.components_admin_productform.ocultar_seo') : t('hardcoded.components_admin_productform.editar_seo')}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Collapse in={showSEO}>
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('hardcoded.components_admin_productform.meta_titulo')}
                                            value={formData.meta_title}
                                            onChange={(e) => handleChange('meta_title', e.target.value)}
                                            helperText={t('hardcoded.components_admin_productform.titulo_que_aparecera_en_google')}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('hardcoded.components_admin_productform.meta_descripcion')}
                                            value={formData.meta_description}
                                            onChange={(e) => handleChange('meta_description', e.target.value)}
                                            multiline
                                            rows={2}
                                            helperText={t('hardcoded.components_admin_productform.resumen_que_aparecera_bajo_el')}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* --- Configuración --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">{i18n.t('navbar.settings')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => handleChange('is_active', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_productform.producto_activo')}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_featured}
                                    onChange={(e) => handleChange('is_featured', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_productform.producto_destacado')}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_digital}
                                    onChange={(e) => handleChange('is_digital', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_productform.producto_digital')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
                <Button onClick={handleSave} variant="contained" size="large">
                    {product ? t('audit.actions.UPDATE') : t('audit.actions.CREATE')} {t('hardcoded.components_admin_productform.producto')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductForm;